/**
 * `window.__spades` under `?e2e=1` (DESIGN-client §3.8). Merged into whatever the store/net agent
 * already put there (`store`, `cueQueue`, `script`): this module only adds scene keys and
 * preserves existing ones. `freezeTime` stops the Pixi ticker and pauses GSAP's global timeline;
 * `step(ms)` advances both deterministically in ≤16.67ms sub-steps.
 */
import { gsap } from 'gsap';
import { Rectangle, type Application } from 'pixi.js';
import type { SceneRoot } from './SceneRoot';
import type { TableLayout } from './layout/solveLayout';

export interface PlayedRecord {
  id: string;
  bus: string;
  gain: number;
  pitch: number;
  delayMs: number;
  /** `performance.now()` at the call. */
  at: number;
  /** false when the engine was not unlocked / the sample was missing (recorded anyway). */
  audible: boolean;
}

export interface SceneTestApi {
  app: Application;
  scene: SceneRoot;
  readonly layout: TableLayout;
  /** RGBA at a CSS-px point of the stage. */
  sample(x: number, y: number): [number, number, number, number];
  pixels(rect: { x: number; y: number; w: number; h: number }): { width: number; height: number; data: number[] };
  /** Stop the ticker and pause GSAP; optionally advance by `ms` right away. */
  freezeTime(ms?: number): void;
  step(ms: number): void;
  timeScale(k: number): void;
  readonly frozen: boolean;
  lastEffect: unknown;
  audio: { lastPlayed: PlayedRecord[]; [k: string]: unknown };
}

type SpadesGlobal = Partial<SceneTestApi> & Record<string, unknown>;

const STEP_MS = 1000 / 60;

export const isE2E = (): boolean =>
  typeof location !== 'undefined' && new URLSearchParams(location.search).get('e2e') === '1';

/** The shared `window.__spades` bag (created on demand). Only meaningful under `?e2e=1`. */
export function testRoot(): SpadesGlobal {
  const w = window as unknown as { __spades?: SpadesGlobal };
  w.__spades ??= {};
  return w.__spades;
}

/** Append-only log the audio engine writes to; safe to call when not under e2e (no-op). */
export function recordPlayed(rec: PlayedRecord): void {
  if (!isE2E()) return;
  const root = testRoot();
  root.audio ??= { lastPlayed: [] };
  root.audio.lastPlayed ??= [];
  root.audio.lastPlayed.push(rec);
  if (root.audio.lastPlayed.length > 500) root.audio.lastPlayed.splice(0, root.audio.lastPlayed.length - 500);
}

export function installTestApi(app: Application, scene: SceneRoot): SceneTestApi | null {
  if (!isE2E()) return null;
  gsap.ticker.lagSmoothing(0);

  let frozen = false;
  let now = 0;

  const freezeTime = (ms = 0): void => {
    if (!frozen) {
      frozen = true;
      app.ticker.stop();
      gsap.globalTimeline.pause();
      now = performance.now();
      app.ticker.update(now);
    }
    if (ms > 0) step(ms);
  };

  const step = (ms: number): void => {
    if (!frozen) freezeTime();
    let remaining = ms;
    while (remaining > 1e-6) {
      const d = Math.min(STEP_MS, remaining);
      remaining -= d;
      now += d;
      app.ticker.update(now);
      // `suppressEvents: false` on purpose: a stepped frame must fire onUpdate/onComplete, or
      // GSAP-driven objects (the count-up Counter) advance visually but never run their callbacks.
      gsap.globalTimeline.time(gsap.globalTimeline.time() + d / 1000, false);
    }
  };

  const timeScale = (k: number): void => {
    app.ticker.speed = k;
    gsap.globalTimeline.timeScale(k);
  };

  const pixels = (rect: { x: number; y: number; w: number; h: number }) => {
    const out = app.renderer.extract.pixels({
      target: app.stage,
      frame: new Rectangle(rect.x, rect.y, Math.max(1, rect.w), Math.max(1, rect.h)),
      resolution: 1,
    });
    return { width: out.width, height: out.height, data: Array.from(out.pixels) };
  };

  const sample = (x: number, y: number): [number, number, number, number] => {
    const p = pixels({ x, y, w: 1, h: 1 }).data;
    return [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0, p[3] ?? 0];
  };

  const root = testRoot();
  const existingAudio = root.audio ?? { lastPlayed: [] };
  existingAudio.lastPlayed ??= [];

  const api: SceneTestApi = {
    app,
    scene,
    get layout() {
      return scene.layout;
    },
    sample,
    pixels,
    freezeTime,
    step,
    timeScale,
    get frozen() {
      return frozen;
    },
    lastEffect: root.lastEffect ?? null,
    audio: existingAudio,
  };
  Object.assign(api as unknown as Record<string, unknown>, {
    missingPipes: () => missingRenderPipes(app),
  });
  // Copy accessors, not values, so `layout`/`frozen` stay live.
  Object.defineProperties(root, Object.getOwnPropertyDescriptors(api));
  return root as SceneTestApi;
}

/**
 * Merge extra keys into `window.__spades` (scene-owned probes such as `cardState`, `shakeOffset`).
 * Descriptors are copied so getters stay live. No-op outside `?e2e=1`.
 */
/**
 * Every `renderPipeId` on the stage that the renderer has no pipe for.
 *
 * Pixi v8 render pipes are opt-in per scene family (`pixi.js/graphics`, `pixi.js/mesh`, …), and a
 * missing one does not fail loudly: `_updateRenderGroups` throws mid-frame, the render loop dies,
 * and the canvas goes blank while the DOM carries on. Enumerating what is actually on screen
 * catches the next missing family without anyone having to think of it in advance.
 */
export function missingRenderPipes(app: Application): Array<[string, number]> {
  const pipes = (app.renderer as unknown as { renderPipes?: Record<string, unknown> }).renderPipes ?? {};
  const missing = new Map<string, number>();
  const walk = (node: unknown): void => {
    const c = node as { renderPipeId?: string; children?: unknown[] } | null;
    if (!c) return;
    if (c.renderPipeId && !(c.renderPipeId in pipes)) {
      missing.set(c.renderPipeId, (missing.get(c.renderPipeId) ?? 0) + 1);
    }
    for (const child of c.children ?? []) walk(child);
  };
  walk(app.stage);
  return [...missing.entries()];
}

export function extendTestApi(extras: Record<string, unknown>): void {
  if (!isE2E()) return;
  Object.defineProperties(testRoot(), Object.getOwnPropertyDescriptors(extras));
}

/** Record what the last triggered effect was (`/dev/juice` buttons, §3.8). */
export function setLastEffect(effect: unknown): void {
  if (!isE2E()) return;
  testRoot().lastEffect = effect;
}
